import { useState } from "react";
import { 
  Tablet, 
  QrCode, 
  Copy, 
  ExternalLink, 
  Download, 
  Share2, 
  MoreVertical, 
  Plus, 
  CheckCircle2,
  Settings,
  Lightbulb,
  Star,
  MonitorPlay,
  ShoppingCart,
  MessageSquare,
  Maximize,
  Lock,
  Power,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

export function TabletInstallInstructions() {
  const [copied, setCopied] = useState(false);
  const { activeRestaurant } = useRestaurantContext();
  const tabletUrl = `${window.location.origin}/menu/${activeRestaurant?.slug}/tablet`;

  const copyLink = () => {
    navigator.clipboard.writeText(tabletUrl);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Tablet className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Tablet de Autoatendimento</CardTitle>
              <CardDescription>
                Instale o app nos tablets das mesas para clientes fazerem pedidos
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="gap-1">
              <MonitorPlay className="h-3 w-3" />
              Screensaver
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <ShoppingCart className="h-3 w-3" />
              Pedidos
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3" />
              Avaliações
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <MessageSquare className="h-3 w-3" />
              Marketing
            </Badge>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Tablet envia direto</AlertTitle>
            <AlertDescription>
              Pedidos feitos no modo <span className="font-mono">/tablet</span> não precisam de aprovação: entram direto em produção e imprimem automaticamente.
              A configuração de aprovação automática no painel vale apenas para o QR Code do celular do cliente.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* QR Code Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Acesso Rápido
          </CardTitle>
          <CardDescription>
            Escaneie o QR Code no tablet para abrir a página de autoatendimento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col items-center gap-4 p-4 bg-white rounded-lg border">
            <QRCodeSVG
              value={tabletUrl}
              size={180}
              level="H"
              includeMargin
            />
            <p className="text-xs text-muted-foreground text-center">
              Escaneie com a câmera do tablet
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 p-2 bg-muted rounded text-sm font-mono truncate">
              {tabletUrl}
            </div>
            <Button variant="outline" size="icon" onClick={copyLink}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" asChild>
              <a href={tabletUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Android Tablet Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.29-.53-2.72-.84-4.29-.84-1.62 0-3.1.32-4.43.87L5.83 5.67c-.18-.29-.54-.38-.83-.22-.3.16-.42.54-.26.85L6.6 9.48C4.16 10.88 2.5 13.2 2.5 15.92h19c0-2.72-1.66-5.04-4.09-6.44zM7 13.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
            </svg>
            Instalação em Tablet Android
          </CardTitle>
          <CardDescription>
            Samsung, Lenovo, Multilaser e outros tablets Android
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Abra o link no Chrome</p>
                <p className="text-sm text-muted-foreground">
                  Use o navegador Chrome para melhor compatibilidade
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Toque no menu
                  <span className="inline-flex items-center justify-center h-6 w-6 bg-muted rounded">
                    <MoreVertical className="h-4 w-4" />
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Ícone de três pontos no canto superior direito
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Selecione "Adicionar à tela inicial"
                  <Download className="h-4 w-4 text-muted-foreground" />
                </p>
                <p className="text-sm text-muted-foreground">
                  Ou "Instalar aplicativo" se disponível
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto!</p>
                <p className="text-sm text-muted-foreground">
                  O app aparecerá na tela inicial do tablet
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* iPad Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Instalação no iPad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Abra o link no Safari</p>
                <p className="text-sm text-muted-foreground">
                  O Safari é necessário para instalar PWAs no iPad
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Toque no botão compartilhar
                  <span className="inline-flex items-center justify-center h-6 w-6 bg-muted rounded">
                    <Share2 className="h-4 w-4" />
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Ícone na parte superior da tela
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium flex items-center gap-2">
                  Selecione "Adicionar à Tela Inicial"
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </p>
                <p className="text-sm text-muted-foreground">
                  Role para baixo se necessário para encontrar a opção
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                4
              </span>
              <div>
                <p className="font-medium">Toque em "Adicionar"</p>
                <p className="text-sm text-muted-foreground">
                  No canto superior direito
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto!</p>
                <p className="text-sm text-muted-foreground">
                  O app aparecerá na tela inicial do iPad
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Daily Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuração Diária
          </CardTitle>
          <CardDescription>
            A cada início de expediente, configure o tablet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Abra o app no tablet</p>
                <p className="text-sm text-muted-foreground">
                  O screensaver será exibido até a configuração
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium">Digite o Código do Dia</p>
                <p className="text-sm text-muted-foreground">
                  Ou solicite liberação manual que será aprovada no PDV
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium">Selecione o número da mesa</p>
                <p className="text-sm text-muted-foreground">
                  Associa o tablet à mesa correta para os pedidos
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto para os clientes!</p>
                <p className="text-sm text-muted-foreground">
                  O tablet está configurado e pronto para uso
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Funcionalidades do Tablet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <MonitorPlay className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-sm">Screensaver Promocional</p>
                <p className="text-xs text-muted-foreground">
                  Exibe banners e promoções quando ocioso
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <ShoppingCart className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-sm">Pedidos pelo Cliente</p>
                <p className="text-xs text-muted-foreground">
                  Cliente navega no cardápio e faz pedidos tocando na tela
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Star className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-sm">Avaliação de Experiência</p>
                <p className="text-xs text-muted-foreground">
                  Sistema de avaliação com 5 perguntas após o pedido
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <MessageSquare className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-sm">Coleta de Dados</p>
                <p className="text-xs text-muted-foreground">
                  Captura nome, email e telefone para marketing
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Dicas de Uso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">1</span>
              </div>
              <div>
                <p className="font-medium text-sm">Mantenha o tablet carregado</p>
                <p className="text-xs text-muted-foreground">
                  Use um suporte com carregador integrado na mesa
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">2</span>
              </div>
              <div>
                <p className="font-medium text-sm">Posicione em local visível</p>
                <p className="text-xs text-muted-foreground">
                  O screensaver atrai atenção com promoções
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary">3</span>
              </div>
              <div>
                <p className="font-medium text-sm">Limpe a tela regularmente</p>
                <p className="text-xs text-muted-foreground">
                  Use pano de microfibra para manter a tela limpa
                </p>
              </div>
            </div>
            <Separator />
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-success">4</span>
              </div>
              <div>
                <p className="font-medium text-sm">Troque a mesa pelo header</p>
                <p className="text-xs text-muted-foreground">
                  Se precisar mover o tablet, toque no header para reconfigurar
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alternative for problematic devices */}
      <Alert className="border-amber-500/50 bg-amber-500/10">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <AlertTitle className="text-amber-600">Opção não aparece no Chrome?</AlertTitle>
        <AlertDescription className="text-sm text-muted-foreground">
          Se a opção "Adicionar à tela inicial" não aparecer, siga estas alternativas:
          <ol className="mt-2 ml-4 list-decimal space-y-1">
            <li>Desative "Site para computador" no menu do Chrome</li>
            <li>Recarregue a página</li>
            <li>Se ainda não funcionar, use o <strong>Fully Kiosk Browser</strong> (recomendado para tablets dedicados)</li>
          </ol>
        </AlertDescription>
      </Alert>

      {/* Fully Kiosk Browser - Recommended for dedicated tablets */}
      <Card className="border-success/30 bg-success/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Maximize className="h-5 w-5 text-success" />
            Recomendado: Fully Kiosk Browser
          </CardTitle>
          <CardDescription>
            Ideal para tablets dedicados que ficam fixos nas mesas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="gap-1 bg-success/10 text-success border-success/30">
              <Maximize className="h-3 w-3" />
              Tela Cheia
            </Badge>
            <Badge variant="secondary" className="gap-1 bg-success/10 text-success border-success/30">
              <Lock className="h-3 w-3" />
              Modo Quiosque
            </Badge>
            <Badge variant="secondary" className="gap-1 bg-success/10 text-success border-success/30">
              <Power className="h-3 w-3" />
              Auto-Iniciar
            </Badge>
          </div>

          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold shrink-0">
                1
              </span>
              <div>
                <p className="font-medium">Instale o Fully Kiosk Browser</p>
                <p className="text-sm text-muted-foreground">
                  Baixe gratuitamente na Play Store
                </p>
                <Button variant="outline" size="sm" className="mt-2" asChild>
                  <a href="https://play.google.com/store/apps/details?id=de.ozerov.fully" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Abrir Play Store
                  </a>
                </Button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold shrink-0">
                2
              </span>
              <div>
                <p className="font-medium">Configure a URL do tablet</p>
                <p className="text-sm text-muted-foreground">
                  Abra o app e insira a URL: <code className="text-xs bg-muted px-1 rounded">{tabletUrl}</code>
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold shrink-0">
                3
              </span>
              <div>
                <p className="font-medium">Ative o Modo Quiosque</p>
                <p className="text-sm text-muted-foreground">
                  Vá em Settings → Kiosk Mode → Enable Kiosk Mode
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold shrink-0">
                4
              </span>
              <div>
                <p className="font-medium">Configure senha de admin</p>
                <p className="text-sm text-muted-foreground">
                  Defina uma senha para sair do modo quiosque (deslize de cima)
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/20 text-success flex items-center justify-center text-sm font-bold shrink-0">
                5
              </span>
              <div>
                <p className="font-medium">Ative auto-iniciar (opcional)</p>
                <p className="text-sm text-muted-foreground">
                  Em Settings → Device Admin → Start on Boot
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-success/10 text-success flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </span>
              <div>
                <p className="font-medium">Pronto!</p>
                <p className="text-sm text-muted-foreground">
                  O tablet funciona em tela cheia, sem interface do navegador
                </p>
              </div>
            </li>
          </ol>

          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium mb-2">Vantagens do Fully Kiosk:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Tela cheia real, sem barras do navegador</li>
              <li>• Bloqueia acesso a outros apps</li>
              <li>• Inicia automaticamente ao ligar o tablet</li>
              <li>• Gratuito para uso básico</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}